function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm overflow-x-auto">
      <code>{children}</code>
    </pre>
  );
}

function EndpointSection({
  method,
  path,
  description,
  children,
}: {
  method: string;
  path: string;
  description: string;
  children: React.ReactNode;
}) {
  const methodColor =
    method === "POST"
      ? "bg-green-600"
      : method === "GET"
        ? "bg-accent"
        : "bg-gray-600";

  return (
    <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
      <div className="flex items-center gap-3 mb-3">
        <span
          className={`px-2 py-1 rounded text-xs font-bold text-white ${methodColor}`}
        >
          {method}
        </span>
        <code className="text-sm font-mono text-gray-900 dark:text-white">
          {path}
        </code>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {description}
      </p>
      {children}
    </div>
  );
}

export function ApiDocsPage() {
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://your-domain.com";

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Ingest API
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Use the Ingest API to programmatically push documents into your
          knowledge base.
        </p>
      </div>

      <div className="space-y-8">
        {/* Base URL */}
        <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Base URL
          </h2>
          <code className="text-sm bg-gray-100 dark:bg-darkmode-700 px-3 py-1.5 rounded text-gray-900 dark:text-white">
            {baseUrl}
          </code>
        </div>

        {/* Authentication */}
        <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Authentication
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            All API requests must include a Bearer token in the Authorization
            header. Generate API tokens in{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              Settings &rarr; API Keys
            </span>
            .
          </p>
          <CodeBlock>{`Authorization: Bearer <your-api-token>`}</CodeBlock>
        </div>

        {/* POST /api/ingest/document */}
        <EndpointSection
          method="POST"
          path="/api/ingest/document"
          description="Upload a document (PDF, Word, Excel, Markdown, or plain text) for processing and indexing. The document will be queued for extraction, chunking, and embedding."
        >
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Parameters
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Content-Type: multipart/form-data
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 pr-4 font-medium text-gray-700 dark:text-gray-300">
                      Field
                    </th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-700 dark:text-gray-300">
                      Type
                    </th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-700 dark:text-gray-300">
                      Required
                    </th>
                    <th className="text-left py-2 font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 dark:text-gray-400">
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      <code>file</code>
                    </td>
                    <td className="py-2 pr-4">File</td>
                    <td className="py-2 pr-4">Yes</td>
                    <td className="py-2">
                      Document file — PDF, DOCX, XLSX/XLS (max 50MB), or UTF-8
                      Markdown / plain text (.md, .markdown, .txt; max 5MB)
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      <code>collectionId</code>
                    </td>
                    <td className="py-2 pr-4">String</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">
                      Add the document to a collection after upload
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <code>metadata</code>
                    </td>
                    <td className="py-2 pr-4">JSON string</td>
                    <td className="py-2 pr-4">No</td>
                    <td className="py-2">
                      Additional metadata stored with the document
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Example
              </h3>
              <CodeBlock>
                {`curl -X POST ${baseUrl}/api/ingest/document \\
  -H "Authorization: Bearer <your-api-token>" \\
  -F "file=@document.pdf" \\
  -F 'metadata={"source":"external-system","version":"1.0"}'`}
              </CodeBlock>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Response (200)
              </h3>
              <CodeBlock>
                {`{
  "documentId": "clx1abc2d0001...",
  "filename": "document.pdf",
  "status": "QUEUED",
  "message": "Document queued for processing"
}`}
              </CodeBlock>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Error Responses
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p>
                  <code className="text-red-600">400</code> — Invalid file type,
                  file too large, or invalid metadata
                </p>
                <p>
                  <code className="text-red-600">401</code> — Missing or invalid
                  API token
                </p>
                <p>
                  <code className="text-red-600">429</code> — Plan document
                  limit exceeded or rate limit hit
                </p>
              </div>
            </div>
          </div>
        </EndpointSection>

        {/* GET /api/ingest/document/:id/status */}
        <EndpointSection
          method="GET"
          path="/api/ingest/document/:id/status"
          description="Poll the processing status of a previously uploaded document."
        >
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Example
              </h3>
              <CodeBlock>
                {`curl ${baseUrl}/api/ingest/document/clx1abc2d0001.../status \\
  -H "Authorization: Bearer <your-api-token>"`}
              </CodeBlock>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Response (200)
              </h3>
              <CodeBlock>
                {`{
  "documentId": "clx1abc2d0001...",
  "filename": "document.pdf",
  "status": "INDEXED",
  "chunkCount": 34,
  "processingStartedAt": "2026-03-27T10:00:00.000Z",
  "processingCompletedAt": "2026-03-27T10:02:15.000Z"
}`}
              </CodeBlock>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Document Statuses
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p>
                  <code>QUEUED</code> — Waiting to be processed
                </p>
                <p>
                  <code>PROCESSING</code> — Text extraction and chunking in
                  progress
                </p>
                <p>
                  <code>PROCESSED</code> — Chunks created, waiting for embedding
                </p>
                <p>
                  <code>INDEXED</code> — Fully indexed and searchable
                </p>
                <p>
                  <code>FAILED</code> — Processing failed
                </p>
              </div>
            </div>
          </div>
        </EndpointSection>
      </div>
    </div>
  );
}
